/**
 * Lenses for JSON data.
 *
 * @module
 */
import { setKey, conservatively, findIndex, DEV_ENV, warning } from './../utils';
import { structEq } from './../equals';
import { Lens } from './';
import { create as createPrism } from './prism';
// @TODO can we optimize this regexp?
const PROP_EXPR_RE = new RegExp([
    '^', 'function', '\\(', '[^), ]+', '\\)', '\\{',
    '("use strict";)?',
    'return\\s',
    '[^\\.]+\\.(\\S+?);?',
    '\\}', '$'
].join('\\s*'));
const WALLABY_PROP_EXPR_RE = new RegExp([
    '^', 'function', '\\(', '[^), ]+', '\\)', '\\{',
    '("use strict";)?',
    '(\\$_\\$wf\\(\\d+\\);)?',
    'return\\s',
    '(\\$_\\$w\\(\\d+, \\d+\\),\\s)?',
    '[^\\.]+\\.(\\S+?);?',
    '\\}', '$'
].join('\\s*'));
export function parsePropertyPath(getterSource) {
    const exprRegexp = process.env.NODE_ENV === 'wallaby' ? WALLABY_PROP_EXPR_RE : PROP_EXPR_RE;
    const exprRegexpGroup = process.env.NODE_ENV === 'wallaby' ? 4 : 2;
    const parse = getterSource.match(exprRegexp);
    if (parse && parse[exprRegexpGroup]) {
        return parse[exprRegexpGroup].split('.');
    }
    else {
        throw new TypeError(`Expected a property expression, got "${getterSource}".

      A property expression should be a referentially transparent (no side-effects),
      single-expression "getter" function.

      Correct example: "function (x) { return x.some }" or "x => x.some".
      Incorrect example: "function (x) { var y = x.some; return y }" or "({some}) => some"`);
    }
}
/**
 * Extract a list of property names from a {@link PropExpr}
 *
 * @param target The target property expressions
 * @example
 * type Second = { two: string }
 * type First = { one: Second }
 * // returns ['one', 'two']
 * extractPropertyNames((x: First) => x.one.two)
 * @returns A list of nested property names
 * @throws Will throw if the target lens expression is of a not valid form
 */
export function extractPropertyPath(target) {
    return parsePropertyPath(target.toString());
}
export function key(k) {
    return k === undefined
        // type-safe key
        ? (k) => Lens.create((s) => s[k], (v, s) => setKey(k, v, s))
        // untyped key
        : Lens.create((s) => s[k], (v, s) => setKey(k, v, s));
}
let propExprDeprecatedWarnings = 0;
function warnPropExprDeprecated(path) {
    // don't warn more than a few times
    if (propExprDeprecatedWarnings < 10) {
        propExprDeprecatedWarnings++;
        const propExpr = `x.${path.join('.')}`;
        const keys = `'${path.join("', '")}'`;
        warning(`The property expression overload of Atom.lens and Lens.prop are deprecated and ` +
            `will be removed in next versions of Focal. Please use the key name overload for ` +
            `Atom.lens and Lens.key instead. ` +
            `You can convert your code by changing the calls:
  a.lens(x => ${propExpr}) to a.lens(${keys}),
  Lens.prop((x: T) => ${propExpr}) to Lens.key<T>()(${keys}).`);
    }
}
/**
 * DEPRECATED: please use Lens.key instead!
 *
 * Create a lens to an object's property. The argument is a property expression, which
 * is a limited form of a getter, with following restrictions:
 * - should be a pure function
 * - should be a single-expression function (i.e. return immediately)
 * - should only access object properties (nested access is OK)
 *
 * @example
 * const obj = { a: { b: 5 } }
 *
 * const l = Lens.prop((x: typeof obj) => x.a.b)
 *
 * l.modify(x => x + 1, obj)
 * // => { a: { b: 6 } }
 * @template TObject type of the object
 * @template TProperty type of the property
 * @param propExpr property get expression
 * @returns a lens to an object's property
 */
export function prop(getter) {
    const path = extractPropertyPath(getter);
    if (DEV_ENV)
        warnPropExprDeprecated(path);
    // @TODO can we optimize this?
    return Lens.compose(...path.map(key()));
}
/**
 * Create a lens that looks at an element at particular index position
 * in an array.
 *
 * @template TItem type of array elements
 * @param i the index
 * @returns a lens to an element at particular position in an array
 */
export function index(i) {
    if (i < 0)
        throw new TypeError(`${i} is not a valid array index, expected >= 0`);
    return createPrism((xs) => xs[i], (v, xs) => {
        if (xs.length <= i) {
            return xs.concat(Array(i - xs.length), [v]);
        }
        else if (structEq(v, xs[i])) {
            return xs;
        }
        else {
            return xs.slice(0, i).concat([v], xs.slice(i + 1));
        }
    });
}
/**
 * Create a lens that will show a given default value if the actual
 * value is absent (is undefined).
 *
 * @param defaultValue default value to return
 */
export function withDefault(defaultValue) {
    // @TODO is this cast safe?
    return Lens.replace(undefined, defaultValue);
}
function choose(getLens) {
    return Lens.create((s) => getLens(s).get(s), (v, s) => getLens(s).set(v, s));
}
/**
 * Create a lens that replaces a given value with a new one.
 */
export function replace(originalValue, newValue) {
    return Lens.create(x => structEq(x, originalValue) ? newValue : x, conservatively((y) => structEq(y, newValue) ? originalValue : y));
}
/**
 * Create a prism that focuses on an array's element that
 * satisfies a given predicate.
 */
export function find(predicate) {
    return choose((xs) => {
        const i = findIndex(xs, predicate);
        return i < 0
            ? Lens.nothing()
            : Lens.index(i);
    });
}
//# sourceMappingURL=json.js.map